import {
  useLayoutEffect,
  useRef,
  useState,
  type JSX,
  type ReactNode,
} from 'react';

import { cn } from '@/lib/utils.js';

/** One chip of the row — `key` identifies it across renders and in the fit result. */
export interface FoldChip {
  key: string;
  node: ReactNode;
}

interface OneRowChipFoldProps {
  /** All chips in priority order — the fold hides from the end, so put the most important first. */
  chips: FoldChip[];
  /**
   * Chip that must stay visible even when the fit would hide it (the active
   * tag filter). When forced in, it takes the last visible slot.
   */
  pinnedKey?: string | null;
  /** Renders the trailing overflow chip — a "+N more" button or a "+N" badge. */
  renderOverflow: (hiddenCount: number) => ReactNode;
  /** Horizontal gap between chips in px — must match the Tailwind gap in `className`. */
  gapPx: number;
  /** Row classes shared by the visible row and the measurement layer (the gap, at minimum). */
  className: string;
  /** Classes for the outer wrapper (e.g. `mt-auto` when the row anchors a card bottom). */
  wrapperClassName?: string;
  'aria-label'?: string;
}

/**
 * Lays chips out on exactly ONE row, hiding the overflow behind a trailing
 * chip from `renderOverflow`. A count-based fold can't promise a single row
 * (12 chips wrap on a narrow pane, 15 fit on a wide one), so this measures:
 * an invisible clone of the full row (plus a worst-case overflow chip)
 * provides natural widths, `clientWidth` of the visible row provides the
 * budget, and a `ResizeObserver` recomputes on pane resize. The measurement
 * runs in a layout effect, so the corrected row is committed before paint —
 * no flash of the wrapped list. Chips are wrapped in `shrink-0` spans so the
 * clone's flex row can't compress them below their natural width.
 */
export function OneRowChipFold({
  chips,
  pinnedKey,
  renderOverflow,
  gapPx,
  className,
  wrapperClassName,
  'aria-label': ariaLabel,
}: OneRowChipFoldProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  /** `null` = everything fits (or not measured yet — the first paint is corrected pre-paint). */
  const [visibleKeys, setVisibleKeys] = useState<string[] | null>(null);
  const chipsKey = chips.map((chip) => chip.key).join('|');

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const compute = () => {
      const chipElements = Array.from(measure.children) as HTMLElement[];
      const overflowElement = chipElements.pop();
      const widths = chipElements.map((element) => element.offsetWidth);
      const overflowWidth = overflowElement?.offsetWidth ?? 0;
      const available = container.clientWidth;
      const rowWidth = (keys: number[]) =>
        keys.reduce(
          (sum, width, index) => sum + width + (index > 0 ? gapPx : 0),
          0,
        );
      /** An unmeasurable container (display:none ancestor, or a layout-less test DOM) must not fold everything away — show the full row instead. */
      if (available <= 0 || rowWidth(widths) <= available) {
        setVisibleKeys(null);
        return;
      }
      const keys = chips.map((chip) => chip.key);
      const budget = available - overflowWidth - gapPx;
      const fitCount = (limit: number): number => {
        let used = 0;
        let count = 0;
        for (const width of widths) {
          const next = used + (count > 0 ? gapPx : 0) + width;
          if (next > limit) break;
          used = next;
          count += 1;
        }
        return count;
      };
      const plainCount = fitCount(budget);
      const pinnedIndex = pinnedKey ? keys.indexOf(pinnedKey) : -1;
      if (pinnedIndex >= 0 && pinnedIndex >= plainCount) {
        const pinnedWidth = widths[pinnedIndex] ?? 0;
        const reservedCount = fitCount(budget - pinnedWidth - gapPx);
        setVisibleKeys([...keys.slice(0, reservedCount), pinnedKey!]);
        return;
      }
      setVisibleKeys(keys.slice(0, plainCount));
    };

    compute();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(compute);
    observer.observe(container);
    return () => observer.disconnect();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- chipsKey stands in for the chips array identity */
  }, [chipsKey, pinnedKey, gapPx]);

  const visible =
    visibleKeys === null
      ? chips
      : chips.filter((chip) => visibleKeys.includes(chip.key));
  const hiddenCount = chips.length - visible.length;

  return (
    <div className={cn('relative', wrapperClassName)}>
      <div
        ref={containerRef}
        aria-label={ariaLabel}
        className={cn(
          'flex flex-nowrap items-center overflow-hidden',
          className,
        )}
      >
        {visible.map((chip) => (
          <span key={chip.key} className="shrink-0">
            {chip.node}
          </span>
        ))}
        {hiddenCount > 0 && (
          <span className="shrink-0">{renderOverflow(hiddenCount)}</span>
        )}
      </div>
      <div
        ref={measureRef}
        aria-hidden="true"
        className={cn(
          'pointer-events-none invisible absolute inset-x-0 top-0 flex flex-nowrap items-center overflow-hidden',
          className,
        )}
      >
        {chips.map((chip) => (
          <span key={chip.key} className="shrink-0">
            {chip.node}
          </span>
        ))}
        <span className="shrink-0">{renderOverflow(chips.length)}</span>
      </div>
    </div>
  );
}
